<template>
  <SimplePageLayout :city="city" title="Short-term rental registration portal" class="bg-light">
    <ErrorModal v-model="error" />
    <div v-if="error && !model" class="p-4 text-danger" role="alert">{{ error.message }}</div>
    <div v-else-if="!model || !form" class="w-100 p-4 d-flex flex-column align-items-center">
      <SpinnerView />
    </div>
    <div v-else-if="!form.published" class="w-100 p-4 text-center h5 mb-0">
      We're sorry. The self-service license application form for {{ city.name }} hasn't been configured yet.
    </div>
    <div v-else class="d-flex flex-column flex-grow-1 h-100">
      <BCollapse :show="currentStepKey !== 'done'">
        <div class="d-flex flex-row py-3 p-md-4">
          <div class="d-flex flex-column w-100 align-items-center gap-1" v-for="(s, i) in steps" :key="`step_${i}`" style="min-width:64px">
            <div class="d-flex flex-row align-items-center w-100">
              <div
                :class="`flex-grow-1 border-top border-2 ${i === 0 ? 'border-clear' : ''}`"
                :style="i <= step && i !== 0 ? `border-color:#${this.cityColor}!important` : ''"
              />
              <div
                class="rounded-circle border border-2 text-center small fw-semibold"
                :style="styleForStep(i)">
                {{ i + 1 }}
              </div>
              <div
                :class="`flex-grow-1 border-top border-2 ${i === steps.length - 1 ? 'border-clear' : ''}`"
                :style="i < step && i !== steps.length - 1 ? `border-color:#${this.cityColor}!important` : ''"
              />
            </div>
            <div :class="`small d-none d-md-block ${step === i ? 'fw-semibold text-center' : ''}`" :style="`color:#${this.cityColor}`">
              {{ s.label }}
            </div>
          </div>
        </div>
      </BCollapse>
      <div class="flex-grow-1 overflow-hidden d-flex flex-column border bg-white rounded-md">
        <div class="overflow-hidden flex-grow-1" v-if="model">
          <div class="h-100 w-100 overflow-auto" ref="scrollable">
            <div class="p-3 p-md-5">
              <!-- Step 1: Address -->
              <AddressRegistrationForm v-if="currentStepKey === 'address'" :model="model" disable-height-limit />

              <!-- Step 2: Owner -->
              <OwnerRegistrationForm v-if="currentStepKey === 'owner'" :model="model" @submit="next" required />

              <!-- Step 3: Listings -->
              <ListingsRegistrationForm v-if="currentStepKey === 'listings'" :model="model" />

              <!-- Step 4: Custom Fields -->
              <CustomRegistrationForm v-if="currentStepKey === 'other'" :model="model" />

              <!-- Step 5: Finish -->
              <ReviewRegistrationForm v-if="currentStepKey === 'review'" :model="model" />

              <!-- Done -->
              <div v-if="Boolean(createdLicense)" class="p-4 d-flex flex-column gap-3">
                <div>
                  Your application has been submitted. Your license application # is <b>{{ createdLicense.id }}</b>
                </div>
                <div>
                  {{ city.licenseApplicationConfirmationText }}
                </div>
                <div v-if="city.licenseApplicationPaymentUrl">
                  <a :href="safeHref(city.licenseApplicationPaymentUrl)" target="_blank" class="btn btn-primary btn-lg" rel="noopener noreferrer">
                    {{ city.licenseApplicationPaymentLabel || 'Pay here' }}
                  </a>
                </div>
                <div>Thank you!</div>
              </div>
            </div>
          </div>
        </div>
        <BCollapse :show="currentStepKey !== 'done'">
          <div class="d-flex flex-row align-items-center justify-content-between py-2 py-md-3 px-3 px-md-5 border-top bg-white">
            <button class="btn btn-flat me-3 fw-semibold" @click.stop.prevent="back" v-if="step > 0">
              <i class="bi-arrow-left"/> Back
            </button>
            <div v-else></div>
            <button class="btn btn-flat fw-semibold" @click.stop.prevent="next" :disabled="!isCurrentStepComplete || submitting" :style="nextButtonStyle">
              <spinner-view v-if="submitting" class="me-2" small />
              <template v-if="step === steps.length - 1">
                Finish
              </template>
              <template v-else>
                Next <i class="bi-arrow-right"/>
              </template>
            </button>
          </div>
        </BCollapse>
      </div>
    </div>
  </SimplePageLayout>
</template>

<script>
import { safeHref } from '@/safeRendering';
import { PublicCity } from "@/models/PublicCity";
import {LicenseEditorModel} from "@/models/LicenseEditorModel";
import SpinnerView from "@/components/common/SpinnerView.vue";
import AddressRegistrationForm from "@/components/register/AddressRegistrationForm.vue";
import OwnerRegistrationForm from "@/components/register/OwnerRegistrationForm.vue";
import ListingsRegistrationForm from "@/components/register/ListingsRegistrationForm.vue";
import BCollapse from "@/components/common/BCollapse.vue";
import CustomRegistrationForm from "@/components/register/CustomRegistrationForm.vue";
import ReviewRegistrationForm from "@/components/register/ReviewRegistrationForm.vue";
import SimplePageLayout from "@/components/common/SimplePageLayout.vue";
import ErrorModal from "@/components/modals/ErrorModal.vue";

export default {
  components: {
    ErrorModal,
    SimplePageLayout,
    ReviewRegistrationForm,
    CustomRegistrationForm,
    BCollapse,
    ListingsRegistrationForm,
    AddressRegistrationForm,
    OwnerRegistrationForm,
    SpinnerView
  },
  props: {
    cityId: String
  },
  data() {
    return {
      model: null,
      submitting: false,
      step: 0,
      error: null,
      createdLicense: null,
      loadSequence: 0
    };
  },
  watch: {
    cityId: {
      immediate: true,
      async handler(id) {
        const sequence = ++this.loadSequence;
        this.model = null;
        this.error = null;
        this.createdLicense = null;
        this.step = 0;

        try {
          const city = await PublicCity.load(id);

          if (sequence === this.loadSequence) {
            this.model = new LicenseEditorModel(city);
          }
        } catch (error) {
          if (sequence === this.loadSequence) {
            this.error = error;
          }
        }
      }
    },

    currentStepKey() {
      if (this.$refs.scrollable) {
        this.$refs.scrollable.scrollTo({top:0});
      }
    }
  },
  methods: {
    safeHref,

    back() {
      this.step = Math.max(0, this.step - 1);
    },
    next() {
      if (this.step < this.steps.length - 1) {
        this.step = this.step + 1;
      } else {
        this.finish();
      }
    },
    async finish() {
      this.submitting = true;
      try {
        this.createdLicense = await this.model.submit();
      } catch (error) {
        this.error = error;
      } finally {
        this.submitting = false;
      }
    },
    styleForStep(step) {
      const selected = this.step === step;
      const done = step < this.step;
      return {
        minWidth: '26px',
        borderColor: `${done || selected ? `#${this.cityColor}` : 'var(--bs-lightgray)'} !important`,
        color: `#${selected ? 'ffffff' : this.cityColor}`,
        backgroundColor: `#${!selected ? 'ffffff' : this.cityColor}`
      };
    }
  },
  computed: {
    form() {
      return this.model && this.model.form;
    },
    city() {
      return this.model && this.model.city;
    },
    cityColor() {
      return (this.city && this.city.backgroundColor) || '333333';
    },
    isCurrentStepComplete() {
      return this.steps[this.step].isComplete();
    },
    nextButtonStyle() {
      if (this.isCurrentStepComplete) {
        return {
          color: `#${this.cityColor}!important`,
          borderColor: `#${this.cityColor}!important`
        };
      } else {
        return {
          color: `#888!important`,
          borderColor: 'transparent'
        };
      }
    },
    currentStepKey() {
      if (!this.createdLicense) {
        return this.steps[this.step].value;
      } else {
        return 'done';
      }
    },
    steps() {
      return [
        {
          value: 'address',
          label: 'Address',
          isComplete: () => this.model.isAddressComplete
        },
        {
          value: 'owner',
          label: 'Contact',
          isComplete: () => this.model.contact.isComplete
        },
        this.model && this.model.form.supportsCustomFields ?
          {
            value: 'other',
            label: 'License Details',
            isComplete: () => this.model.isCustomComplete
          } : null,
        {
          value: 'listings',
          label: 'Listings',
          isComplete: () => true
        },
        {
          value: 'review',
          label: 'Review',
          isComplete: () => true
        }
      ].filter(step => !!step);
    }
  }
}
</script>

<style>
.sd-root-modern .sd-container-modern .sd-body {
  padding: 2px !important;
  margin: 0 !important;
  max-width: calc(100% - 4px);
}
label.form-label {
  font-size: 16px;
}
</style>
